import cn from "classnames";

import type { FreeQuoteContent } from "@/components/freeQuoteDrawer/freeQuoteDrawer.component";
import { mergeFreeQuoteDrawerContent } from "@/components/freeQuoteDrawer/freeQuoteDrawer.defaults";
import { FreeQuoteForm } from "@/components/freeQuoteDrawer/freeQuoteForm.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";

export const FreeQuoteInline = ({ block }: FreeQuoteInlineProps) => {
  const content = mergeFreeQuoteDrawerContent(block.drawer);
  const isDark = block.theme === "darkBlue";
  const showInstallationPreference = block.installationPreference === "shown";
  const showEmployeePromoCode = block.employeePromoCode === "shown";
  const showSocialPlatformReferralSource =
    block.socialPlatformReferralSource === "shown";

  return (
    <section
      aria-labelledby="free-quote-inline-title"
      className={
        isDark
          ? "bg-secondary w-full py-16 text-white xl:py-20"
          : "border-greyscale-strength/40 border-y bg-white py-12 lg:py-14"
      }>
      <div className="container mx-auto w-[calc(100%_-_2rem)] px-4">
        {isDark ? null : block.callout ? (
          <p className="text-greyscale-body text-center text-xs font-semibold tracking-[0.14em] uppercase">
            {block.callout}
          </p>
        ) : null}

        {isDark ? null : (
          <Text
            id="free-quote-inline-title"
            variant="h4"
            className="text-greyscale-headline text-center text-[30px]! leading-[1.15]! sm:text-[34px]!">
            {block.heading}
          </Text>
        )}

        {isDark ? null : (
          <div className="text-greyscale-body mx-auto mt-2 max-w-4xl text-center text-sm leading-[1.45] sm:text-base [&_a]:font-semibold [&_a]:text-[#2d68ad] [&_a]:underline [&_a]:underline-offset-2">
            <RichText children={content.intro} />
          </div>
        )}

        {isDark ? null : (
          <div className="text-greyscale-body mx-auto mt-3 max-w-5xl text-center text-xs leading-[1.45]">
            <RichText children={content.consentText} />
          </div>
        )}

        {isDark ? null : (
          <p className="text-greyscale-body mt-6 text-center text-xs">
            {content.requiredFieldsMessage}
          </p>
        )}

        <div className={cn(!isDark && "mt-5")}>
          <FreeQuoteForm
            content={content}
            consentText={block.consentText ? block.consentText : ""}
            variant="inline"
            theme={block.theme ?? "standard"}
            formLayout={
              block.formLayout === "twoColumn"
                ? "threeColumn"
                : (block.formLayout ??
                  (block.employeePromoCode != null ||
                  block.socialPlatformReferralSource != null
                    ? "threeColumn"
                    : "horizontal"))
            }
            showInstallationPreference={showInstallationPreference}
            showEmployeePromoCode={showEmployeePromoCode}
            showSocialPlatformReferralSource={showSocialPlatformReferralSource}
            banner={
              isDark ? (
                <>
                  {block.callout ? (
                    <p
                      className={cn(
                        brown.className,
                        "text-primary text-sm font-bold tracking-[0.08em] uppercase",
                      )}>
                      {block.callout}
                    </p>
                  ) : null}
                  <h2
                    id="free-quote-inline-title"
                    className={cn(
                      brown.className,
                      "mt-3 text-[40px] leading-[1.1] font-bold text-white lg:text-[48px]",
                    )}>
                    {block.heading}
                  </h2>
                  <div className="mt-4 max-w-6xl text-base leading-snug sm:text-lg [&_a]:font-semibold [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2">
                    <RichText children={block.intro ?? content.intro} />
                  </div>
                </>
              ) : undefined
            }
          />
        </div>
      </div>
    </section>
  );
};

export type FreeQuoteInlineProps = {
  block: {
    heading: string;
    intro: string | null;
    consentText?: string | null;
    theme?: "standard" | "darkBlue" | null;
    formLayout?: "horizontal" | "threeColumn" | "twoColumn" | null;
    callout?: string | null;
    installationPreference?: "hidden" | "shown" | null;
    employeePromoCode?: "hidden" | "shown" | null;
    socialPlatformReferralSource?: "hidden" | "shown" | null;
    drawer?: FreeQuoteContent | null;
  };
};
